import {Theme} from '@mui/material';
import {Localization} from '@mui/material/locale';
import {createTheme} from '@mui/material/styles';

import {alert, red, black, canvasGray, coolGray, green, metallicSeaweed, purple, white} from './merieColors';
import {merieCoolGray} from '@wallaby-ui/haidi/src/js/config/theme/haidiColors';

const themeVariables = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1366,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: 'SourceSansPro-Regular',
    fontWeightExtraLight: 200,
    fontWeightSemiBold: 400,
    basicText: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '22px',
    },
    basicTextBold: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    emptyValue: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      fontStyle: 'italic',
      lineHeight: '18px',
    },
    emptyValueBold: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '14px',
      fontStyle: 'italic',
      lineHeight: '14px',
    },
    value: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '18px',
    },
    valueBold: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    valueSmall: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '12px',
      lineHeight: '12px',
    },
    label: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    labelBold: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    sectionHeader: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '14px',
      lineHeight: '18px',
    },
    cardHeader: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '18px',
      lineHeight: '23px',
    },
    caption: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '12px',
      lineHeight: '22px',
    },
    index: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '10px',
      lineHeight: '13px',
    },
    chipButton: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '12px',
      lineHeight: '22px',
    },
    popper: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '12px',
      lineHeight: '15px',
    },
    textButtonRegular: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '22px',
    },
    textButton: {
      fontFamily: 'SourceSansPro-Semibold',
      fontSize: '14px',
      lineHeight: '22px',
    },
    patientCardName: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '20px',
      lineHeight: '25px',
    },
    riskScoreValue: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '34px',
      lineHeight: '26px',
    },
    riskScoreChange: {
      fontFamily: 'SourceSansPro-Bold',
      fontSize: '11px',
      lineHeight: '10px',
    },
    userName: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '17.6px',
    },
    loginMenu: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '18px',
    },
    hierarchyLabel: {
      fontFamily: 'SourceSansPro-Regular',
      fontSize: '14px',
      lineHeight: '18px',
    },
  },
  spacing: 4,
  borderRadius: '16px',
  direction: 'ltr',
  palette: {
    custom: {
      red,
      purple,
      theme: metallicSeaweed,
      themeGray: coolGray,
      metallicSeaweed,
      coolGray,
      white,
      black,
      canvasGray,
      alert,
      green,

      navigationBox: metallicSeaweed['700'],
      navigationHeader: metallicSeaweed['200'],
      navigationHeaderIcon: metallicSeaweed['200'],
      navigationItemAvatar: metallicSeaweed['500'],
      navigationButton: metallicSeaweed['300'],
      navigationButtonHover: metallicSeaweed['400'],
      dividerBorderColor: coolGray['500'],
      userSection: metallicSeaweed['700'],
      userSectionHover: metallicSeaweed['900'],
      userSectionHoverText: metallicSeaweed['300'],
      userMenu: white,
      userMenuRow: '#ffffff',
      userMenuRowHover: coolGray['50'],
      userMenuRowPressed: coolGray['50'],
      userMenuIcon: metallicSeaweed['400'],
      userMenuBorder: coolGray['100'],
      threeDotsColor: coolGray['500'],
      threeDotsColorHover: '#ffffff',
      removeItemIcon: metallicSeaweed['300'],
      fixedPaginationButtonBgColor: coolGray['50'],
      fixedPaginationButtonIcon: metallicSeaweed['500'],
      fixedPaginationButtonIconBig: metallicSeaweed['700'],
      fixedPaginationButtonIconDisabled: coolGray['500'],
      fixedPaginationButtonIconDisabledBig: coolGray['300'],
      fixedPaginationButtonIconBorder: coolGray['300'],
      headerLabel: coolGray['900'],

      dlMuiTextFieldColor: coolGray['100'],
      dlMuiTextFieldTextColor: coolGray['500'],
      dlMuiTextFieldTextColorHoverFocus: coolGray['500'],
      dlMuiTextFieldBorderColor: coolGray['100'],
      dlMuiTextFieldBorderColorHover: coolGray['400'],
      dlMuiTextFieldBorderColorFocus: metallicSeaweed['500'],

      dlHierarchyNodeItemText: coolGray['800'],
      dlHierarchyNodeItemTextFocus: white,
      dlHierarchyNodeItemTextFocusHover: white,

      dlHierarchyNodeItemHover: canvasGray,
      dlHierarchyNodeItemFocus: metallicSeaweed['500'],
      dlHierarchyNodeItemFocusHover: metallicSeaweed['600'],
      dlHierarchyNodeItemDisabledBackground: white,
      dlHierarchyNodeItemDisabled: coolGray['500'],
    },
  },
  components: {},
});

export const theme = createTheme(themeVariables, {
  components: {
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },

      styleOverrides: {
        iconStandard: {
          right: 8,
          top: 12,
        },
        select: {
          padding: '8.5px 48px 8.5px 14px !important',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          'color': themeVariables.palette.custom.coolGray['800'],
          '&:active, &:visited, &:hover, &:focus': {
            backgroundColor: 'var(--dlAgGridColumnHover)',
            color: themeVariables.palette.custom.coolGray['800'],
          },
          '&:active:focus, &:active:hover': {
            backgroundColor: themeVariables.palette.custom.metallicSeaweed['600'],
            color: themeVariables.palette.custom.white,
            outline: 'none',
          },
          '&[aria-selected="true"]': {
            'backgroundColor': themeVariables.palette.custom.metallicSeaweed['500'],
            'color': themeVariables.palette.custom.white,
            '&:active, &:visited, &:focus': {
              backgroundColor: themeVariables.palette.custom.metallicSeaweed['500'],
              color: themeVariables.palette.custom.white,
            },
            '&:hover': {
              backgroundColor: themeVariables.palette.custom.metallicSeaweed['600'],
              color: themeVariables.palette.custom.white,
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        popupIndicator: {
          top: 0,
        },
        clearIndicator: {
          top: 0,
        },
      },
    },
    MuiInput: {
      defaultProps: {
        disableUnderline: true,
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          paddingTop: 5,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          ...themeVariables.typography.value,
          'color': coolGray['900'],
          '& label': {
            color: coolGray['900'],
          },
          '& label.Mui-focused': {
            color: metallicSeaweed['600'],
          },
          '& .MuiOutlinedInput-root': {
            ...themeVariables.typography.value,
            color: coolGray['900'],
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        sizeSmall: {
          marginTop: 0,
          lineHeight: '12px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          // Set to 0 because it interferes with clickable area in TextField
          zIndex: 0,
        },
        sizeSmall: {
          paddingRight: 30,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        clickable: {
          ...themeVariables.typography.chipButton,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiSlider: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        markLabel: {
          top: '20px',
        },
        valueLabel: {
          top: '-10px',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-flexContainer': {
            gap: '16px',
          },
          'button.Mui-selected': {
            'color': themeVariables.palette.custom.white,
            'backgroundColor': themeVariables.palette.custom.metallicSeaweed['500'],
            '& .MuiTab-iconWrapper': {
              color: themeVariables.palette.custom.white,
            },
            '&:hover': {
              backgroundColor: themeVariables.palette.custom.metallicSeaweed['600'],
            },
            '&:active': {
              backgroundColor: themeVariables.palette.custom.metallicSeaweed['700'],
            },
          },
          'button': {
            textTransform: 'none',
          },
        },
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...themeVariables.typography.cardHeader,
          'color': themeVariables.palette.custom.coolGray['800'],
          'gap': '8px',
          'borderRadius': '8px',
          'minHeight': 'auto',
          'padding': '8px 12px',
          '& .MuiTab-iconWrapper': {
            color: themeVariables.palette.custom.metallicSeaweed['300'],
          },
          '&:hover': {
            'backgroundColor': themeVariables.palette.custom.metallicSeaweed['600'],
            'color': themeVariables.palette.custom.white,
            '& .MuiTab-iconWrapper': {
              color: themeVariables.palette.custom.white,
            },
          },
          '&:active': {
            'backgroundColor': themeVariables.palette.custom.metallicSeaweed['700'],
            'color': themeVariables.palette.custom.white,
            '& .MuiTab-iconWrapper': {
              color: themeVariables.palette.custom.white,
            },
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          'color': themeVariables.palette.custom.coolGray['800'],
          '&.Mui-checked': {
            // This will apply only to checked state
            color: themeVariables.palette.custom.metallicSeaweed['500'],
          },
          '&:hover': {
            backgroundColor: themeVariables.palette.custom.metallicSeaweed['100'],
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          'margin': 0,
          '& .MuiRadio-root': {
            padding: 0,
          },
          '& .MuiFormControlLabel-label': {
            paddingLeft: '4px',
          },
        },
      },
    },
  },
});

export function dlMuiCreateThemeFunction(theme: Theme, localeData: Localization) {
  return createTheme(theme, localeData);
}
